import { Fragment, memo, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiAvatarCard, MiBadge } from "@miplatform/components";
import { getDocumentUrl, getNameAbbreviation } from "@miplatform/utils";
import { mdiHomePlus } from "@mdi/js";
import { Box, Divider, Menu, Typography } from "@mui/material";
import { MiAttemptNumberBadge } from "../../../../../components/MiAttemptNumberBadge/MiAttemptNumberBadge";
import { CLOSEOUT_SCREENS as SCREENS } from "../constants";

export const CloseoutDrawerHeader = memo(
  ({
    isATakeover,
    assignment = {},
    attemptNumber = "",
    contacts = [],
    user,
    activeScreen,
    issueIndex,
    issues = [],
    issuesLength,
    unfilteredIssuesLength,
    selectedIssue,
  }) => {
    const { name: userName } = user || {};

    const { assignmentTemplateName = "", assignmentNumber = "" } =
      assignment || {};

    const { classes, theme } = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const selectedIssueIndex = useMemo(() => {
      if (!selectedIssue?.id) {
        return -1;
      }

      return issues.findIndex((issue) => issue.id === selectedIssue.id);
    }, [issues, selectedIssue]);

    const getTitle = () => {
      if (activeScreen === SCREENS.REVIEW) {
        return `Summary: #${assignmentNumber}`;
      }

      if (activeScreen === SCREENS.CLOSEOUT) {
        if (selectedIssue) {
          if (selectedIssueIndex >= 0) {
            return `Task Details ${selectedIssueIndex + 1}/${issuesLength}`;
          }

          return "Task Details";
        }
        return `Closeout ${issueIndex + 1}/${issuesLength}`;
      }

      if (activeScreen === SCREENS.PREBILL) {
        return `Pre-Bill ${issueIndex + 1}/${unfilteredIssuesLength}`;
      }

      return "Order Details";
    };

    const upperLeftText = useMemo(() => {
      if (activeScreen === SCREENS.REVIEW) {
        return "Order Review";
      }

      return assignmentTemplateName;
    }, [activeScreen, assignment]);

    const upperRightText = useMemo(() => {
      if (activeScreen === SCREENS.REVIEW) {
        return "Task Detail";
      }

      return assignmentNumber;
    }, [activeScreen, assignment]);

    return (
      <Box className={classes.root}>
        <Box className={classes.leftContainer}>
          <Box className={classes.infoContainer}>
            <Typography fontSize={theme.fontSize.medium}>
              {upperLeftText} &nbsp; / &nbsp;
              <Typography
                component="span"
                fontSize={theme.fontSize.medium}
                color={theme.palette.primary.main}
                fontWeight={theme.typography.fontWeightBold}
              >
                {upperRightText}
              </Typography>
            </Typography>
            <MiAttemptNumberBadge attemptNumber={attemptNumber} />
          </Box>
          <Box className={classes.infoContainer}>
            <Typography fontWeight={theme.fontWeight.semiBold}>
              {getTitle()}
            </Typography>
            {isATakeover && (
              <MiBadge
                className={classes.takeoverBadge}
                backgroundColor={theme.palette.orange.medium}
                text="Takeover"
                fontSize={theme.fontSize.xsmall}
                icon={mdiHomePlus}
                iconColor={theme.palette.common.white}
                iconSize={0.5}
              />
            )}
          </Box>
        </Box>
        <Box className={classes.infoContainer}>
          {userName && (
            <MiAvatarCard
              text="Service Tech"
              subtext={userName}
              src={getDocumentUrl(user?.profilePicture?.id)}
            />
          )}
          <MiAvatarCard
            isDropDown={contacts.length > 0}
            text="Click to view"
            subtext={`${contacts.length} Contacts`}
            onClick={handleMenuClick}
          />
          <Menu
            id="lock-menu"
            anchorEl={anchorEl}
            open={anchorEl && contacts.length > 0}
            onClose={handleClose}
            MenuListProps={{ sx: { py: 0, maxHeight: 300 } }}
          >
            {contacts.map(({ id, firstName, lastName, label }) => (
              <Fragment key={id}>
                <MiAvatarCard
                  text={label}
                  subtext={getNameAbbreviation(firstName, lastName)}
                />
                <Divider />
              </Fragment>
            ))}
          </Menu>
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  leftContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  infoContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  container: {
    margin: `${theme.spacing(1)} ${theme.spacing(4)} 0`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "max-width",
  },
  takeoverBadge: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${theme.palette.orange.darkAccent}`,
    fontSize: theme.typography.pxToRem(11),
    height: theme.spacing(5),
    marginLeft: theme.spacing(1),
  },
}));
